"use client";
import React, { useEffect, useState } from "react";
import "@/styles/components/privacy_policy.scss";

import Navbar from "@/components/common/Navbar";
import Footer from "@/components/common/Footer";
import Image from "next/image";

const termsData = [
  {
    id: "use-services",
    title: "Use of the Services",
    items: [
      {
        id: "eligibility",
        title: "Eligibility",
        content: [
          "You must be at least 18 years old or have legal parental/guardian consent to use the Services. By using Baskitt, you represent that you meet these requirements.",
        ],
      },
      {
        id: "account-registration",
        title: "Account Registration",
        content: [
          "To access certain features, you must create an account. You agree to:",
          [
            "Provide accurate and complete information",
            "Keep your information updated",
            "Maintain the confidentiality of your login credentials",
          ],
          "You are responsible for all activities conducted under your account.",
        ],
      },
      {
        id: "acceptable-use",
        title: "Acceptable Use",
        content: [
          "You agree not to:",
          [
            "Use the Services for unlawful purposes",
            "Submit false or misleading information",
            "Attempt unauthorized access to our systems",
            "Interfere with the operation or security of the platform",
            "Upload malicious software or harmful code",
          ],
          "We reserve the right to suspend or terminate accounts that violate these Terms.",
        ],
      },
      {
        id: "platform-role",
        title: "Platform Role",
        content: [
          "Baskitt operates as an intermediary platform connecting users with third-party retailers, sellers, and service providers. Unless explicitly stated, Baskitt does not manufacture, own, or directly control the products listed by retailers.",
        ],
      },
    ],
  },

  {
    id: "orders",
    title: "Orders, Payments & Transactions",
    items: [
      {
        id: "orders-section",
        title: "Orders",
        content: [
          "All orders placed through Baskitt are subject to acceptance by the respective retailer. Baskitt does not guarantee product availability, pricing accuracy, or delivery timelines.",
        ],
      },
      {
        id: "pricing",
        title: "Pricing",
        content: [
          "Prices displayed may change without notice. While we strive for accuracy, errors in pricing or product descriptions may occur. Baskitt reserves the right to cancel or refuse orders due to pricing errors or inaccuracies.",
        ],
      },
      {
        id: "payments",
        title: "Payments",
        content: [
          "Payments are processed securely via third-party payment providers. Baskitt:",
          [
            "Does not store full credit card details",
            "Is not responsible for payment gateway failures",
            "Is not liable for banking errors or transaction delays",
          ],
        ],
      },
      {
        id: "refunds",
        title: "Cancellations & Refunds",
        content: [
          "Refund and cancellation policies may vary depending on the retailer. Users must review the applicable retailer’s policy before completing a purchase.",
        ],
      },
      {
        id: "third-party",
        title: "Third-Party Responsibility",
        content: [
          "Baskitt is not responsible for:",
          [
            "Product quality",
            "Product legality",
            "Shipping delays",
            "Retailer conduct",
          ],
          "Disputes related to products must be addressed with the retailer unless otherwise specified.",
        ],
      },
    ],
  },

  {
    id: "disclaimers",
    title: "Disclaimers & Limitation of Liability",
    items: [
      {
        id: "as-is",
        title: "“As-Is” Basis",
        content: [
          "The Services are provided on an 'as-is' and 'as-available' basis without warranties of any kind, whether express or implied.",
          "We do not guarantee:",
          [
            "Continuous or uninterrupted access",
            "Error-free functionality",
            "Complete accuracy of listings or content",
          ],
        ],
      },
      {
        id: "liability",
        title: "Limitation of Liability",
        content: [
          "To the maximum extent permitted by law, Baskitt shall not be liable for:",
          [
            "Indirect, incidental, or consequential damages",
            "Loss of profits, revenue, data, or business opportunities",
            "Damages arising from third-party retailers or service providers",
            "Losses caused by events beyond our reasonable control",
          ],
          "Your use of the Services is at your own risk.",
        ],
      },
      {
        id: "technical-errors",
        title: "Technical Errors",
        content: [
          "The platform may contain technical, typographical, or informational errors. Baskitt reserves the right to correct such errors without prior notice.",
        ],
      },
    ],
  },

  {
    id: "intellectual",
    title: "Intellectual Property & Platform Rights",
    items: [
      {
        id: "ownership",
        title: "Ownership",
        content: [
          "All content on Baskitt, including:",
          ["Logos", "Branding", "Software", "Designs", "Text", "Graphics"],
          "is owned by or licensed to Baskitt and protected by applicable intellectual property laws.",
        ],
      },
      {
        id: "restrictions",
        title: "Restrictions",
        content: [
          "You may not:",
          [
            "Copy, reproduce, distribute, or modify content",
            "Reverse engineer the platform",
            "Use Baskitt branding without written consent",
          ],
        ],
      },
      {
        id: "user-content",
        title: "User Content",
        content: [
          "If you submit reviews, comments, or other content, you grant Baskitt a non-exclusive, worldwide, royalty-free license to use, reproduce, and display such content in connection with the Services.",
        ],
      },
    ],
  },

  {
    id: "termination",
    title: "Termination, Modifications & General Provisions",
    items: [
      {
        id: "termination-user",
        title: "Termination by User",
        content: [
          "You may stop using the Services at any time. Account deletion requests are governed by our Privacy Policy.",
        ],
      },
      {
        id: "termination-baskitt",
        title: "Termination by Baskitt",
        content: [
          "We reserve the right to suspend or terminate accounts:",
          [
            "For violations of these Terms",
            "For unlawful activity",
            "For misuse of the platform",
            "If required by law",
          ],
          "Termination may result in restricted access to your account and Services.",
        ],
      },
      {
        id: "modifications",
        title: "Modifications",
        content: [
          "We may modify these Terms at any time. Updated Terms become effective immediately upon posting.",
          "Continued use of the Services constitutes acceptance of the revised Terms.",
        ],
      },
      {
        id: "governing-law",
        title: "Governing Law",
        content: [
          "These Terms shall be governed by and interpreted in accordance with the laws of the jurisdiction in which Baskitt operates.",
        ],
      },
      {
        id: "severability",
        title: "Severability",
        content: [
          "If any provision of these Terms is found unenforceable, the remaining provisions shall remain in full force and effect.",
        ],
      },
      {
        id: "entire-agreement",
        title: "Entire Agreement",
        content: [
          "These Terms constitute the entire agreement between you and Baskitt regarding use of the Services.",
        ],
      },
    ],
  },
  {
    id: "contact",
    title: "Contact Information",
    items: [
      {
        id: "contact-details",
        title: "Contact Information",
        content: [
          "For legal inquiries:",
          "Baskitt App",
          "Email: website@baskitt.co",
          "Website: www.baskitt.co",
        ],
      },
    ],
  },
];

export default function TermsOfUse() {
  const [active, setActive] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      termsData.forEach((section) => {
        const firstItem = document.getElementById(section.items[0].id);
        const lastItem = document.getElementById(
          section.items[section.items.length - 1].id,
        );

        if (!firstItem || !lastItem) return;

        const top = firstItem.offsetTop;
        const bottom = lastItem.offsetTop + lastItem.offsetHeight;

        if (scrollPosition >= top && scrollPosition < bottom) {
          setActive(section.id);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const renderContent = (content) => {
    if (typeof content === "string") return <p>{content}</p>;

    if (Array.isArray(content)) {
      return content.map((item, index) => {
        if (Array.isArray(item)) {
          return (
            <ul key={index}>
              {item.map((li, i) => (
                <li key={i}>{li}</li>
              ))}
            </ul>
          );
        }
        return <p key={index}>{item}</p>;
      });
    }
  };

  return (
    <>
      <div className="section navbar_section">
        <Navbar />
      </div>

      {/* HEADER */}
      <section id="policy_header">
        <h1>
          Terms of <span>Service</span>
        </h1>

        <div className="box_image">
          <Image
            src="/images/privacy-policy.png"
            width={422}
            height={282}
            alt="box"
          />
        </div>
      </section>

      {/* CONTENT */}
      <section id="policy_section">
        <div className="policy_wrapper">
          {/* SIDEBAR */}
          <div className="policy_sidebar">
            <ul>
              {termsData.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.items[0].id}`}
                    className={active === section.id ? "active" : ""}
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          {/* CONTENT */}
          <div className="policy_content">
            {termsData.map((section, sectionIndex) => (
              <div key={section.id}>
                <h2>
                  {sectionIndex + 1}. {section.title}
                </h2>

                {section.items.map((item, itemIndex) => (
                  <section key={item.id} id={item.id}>
                    <h3>
                      {sectionIndex + 1}.{itemIndex + 1} {item.title}
                    </h3>

                    {renderContent(item.content)}
                  </section>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
